// frontend/src/features/tbs/tbsApi.js
// Thin wrappers over the /tbs API. All binary downloads go through axios so
// the bearer token is sent (a bare <a href> cannot set Authorization).
import api from "../../apiClient";

export async function getCollectors() {
  const { data } = await api.get("/tbs/collectors");
  return data;
}

export async function previewInput({ text, file, defaultCollector }) {
  const fd = new FormData();
  if (file) fd.append("file", file);
  if (text) fd.append("text", text);
  if (defaultCollector) fd.append("default_collector", defaultCollector);
  const { data } = await api.post("/tbs/preview", fd, { timeout: 60000 });
  return data;
}

export async function createJob(payload) {
  const { data } = await api.post("/tbs/jobs", payload, { timeout: 60000 });
  return data;
}

export async function listJobs(limit = 30) {
  const { data } = await api.get("/tbs/jobs", { params: { limit } });
  return data.jobs || [];
}

export async function getJob(id) {
  const { data } = await api.get(`/tbs/jobs/${id}`);
  return data;
}

export async function cancelJob(id) {
  const { data } = await api.post(`/tbs/jobs/${id}/cancel`);
  return data;
}

export async function retryJob(id) {
  const { data } = await api.post(`/tbs/jobs/${id}/retry`);
  return data;
}

export async function deleteJob(id) {
  const { data } = await api.delete(`/tbs/jobs/${id}`);
  return data;
}

export async function getHealth() {
  const { data } = await api.get("/tbs/health");
  return data;
}

export async function runProbe(includeHeavy = false) {
  const { data } = await api.post("/tbs/health/probe", { include_heavy: includeHeavy });
  return data;
}

async function fetchBlob(url, timeout = 120000) {
  const res = await api.get(url, { responseType: "blob", timeout });
  const cd = res.headers?.["content-disposition"] || "";
  const m = /filename="?([^";]+)"?/i.exec(cd);
  return { blob: res.data, filename: m ? m[1] : null };
}

export async function openRowPdf(jobId, rowId) {
  const { blob } = await fetchBlob(`/tbs/jobs/${jobId}/rows/${rowId}/pdf`);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function downloadRowPdf(jobId, rowId, fallbackName) {
  const { blob, filename } = await fetchBlob(`/tbs/jobs/${jobId}/rows/${rowId}/pdf`);
  saveBlob(blob, filename || fallbackName || "statement.pdf");
}

// `year` limits the archive to statements verified as that tax year; the
// server lists everything it left out in manifest.json.
export async function downloadZip(jobId, year = null) {
  const q = year ? `?year=${encodeURIComponent(year)}` : "";
  const { blob, filename } = await fetchBlob(`/tbs/jobs/${jobId}/zip${q}`, 300000);
  saveBlob(blob, filename || `tax_bills_job${jobId}${year ? `_${year}only` : ""}.zip`);
}

export async function downloadData(jobId) {
  const { blob, filename } = await fetchBlob(`/tbs/jobs/${jobId}/data.xlsx`);
  saveBlob(blob, filename || `tax_bill_data_job${jobId}.xlsx`);
}

export function errorText(e, fallback = "Request failed") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object" && d.message) return d.message;
  return e?.message || fallback;
}
